import { Injectable } from '@angular/core';
import { Rider } from '@interfaces/models.interface';
import { ExcelService } from '@services/excel.service';

export type DownloadModuleKey =
    | 'partners'
    | 'customers'
    | 'admins'
    | 'topups'
    | 'withdrawals'
    | 'coupons'
    | 'rewards'
    | 'ads'
    | 'news_events'
    | (string & {});
export type PartnerReportTemplate = 'template_1' | 'template_2';
export type DownloadHandlerArgs = PartnerReportDownloadArgs | GenericReportDownloadArgs | Record<string, unknown>;

export interface PartnerReportDownloadArgs {
    template: PartnerReportTemplate;
    reportDate: string;
    data: Rider[];
}

export interface GenericReportDownloadArgs {
    data: any[];
    fileName?: string;
}

export interface DownloadHandlerResult {
    message: string;
}

type ExcelRow = Record<string, string | number>;
type RiderWithUpdateDate = Rider & { dateUpdated?: string };

@Injectable({
    providedIn: 'root',
})
export class DownloadHandlerUtil {
    constructor(private excelService: ExcelService) {}

    downloadReport(moduleKey: DownloadModuleKey, args: DownloadHandlerArgs): DownloadHandlerResult {
        switch (moduleKey) {
            case 'partners':
                return this.downloadPartnerReport(args as PartnerReportDownloadArgs);
            case 'customers':
                return this.downloadGenericReport(moduleKey, args as GenericReportDownloadArgs, 'customers-report', (item) => ({
                    'Customer ID': this.readValue(item, ['membershipId', 'userId']),
                    'Name': this.readValue(item, ['name']),
                    'Mobile Number': this.readValue(item, ['mobile']),
                    'Email': this.readValue(item, ['email']),
                    'Date Created': this.readValue(item, ['dateCreated']),
                    'Current Pts': this.readValue(item, ['currentPoints']),
                    'Used Pts': this.readValue(item, ['usedPoints']),
                    'Total Pts': this.toNumber(item?.usedPoints) + this.toNumber(item?.currentPoints),
                    'Status': item?.isVerify ? 'Verified' : 'Unverified',
                }));
            case 'admins':
                return this.downloadGenericReport(moduleKey, args as GenericReportDownloadArgs, 'admins-report', (item) => ({
                    'Name': this.readValue(item, ['name']),
                    'Email': this.readValue(item, ['email']),
                    'Registration Date': this.readValue(item, ['dateCreated']),
                    'Status': item?.status === 1 || item?.status === 'active' ? 'Active' : this.formatPartnerStatus(String(item?.status || 'inactive')),
                }));
            case 'topups':
                return this.downloadGenericReport(moduleKey, args as GenericReportDownloadArgs, 'topups-report', (item) => ({
                    'Transaction ID': this.readValue(item, ['topUpRequestId', 'topupRequestId', 'transactionId']),
                    'Partner Code': this.readNestedValue(item, ['partner.membershipId', 'partner.partnerId', 'partnerId']),
                    'Partner Name': this.getFullNameFromObject(item?.partner) || this.readValue(item, ['partnerName']),
                    "Partner's Mobile Number": this.readNestedValue(item, ['partner.mobile', 'mobile']),
                    'Date': this.readValue(item, ['dateCreated']),
                    'Amount': this.readValue(item, ['amount']),
                    'Top-up Type': this.readValue(item, ['source', 'type']),
                }));
            case 'withdrawals':
                return this.downloadGenericReport(moduleKey, args as GenericReportDownloadArgs, 'withdrawals-report', (item) => ({
                    'Ref No.': this.readValue(item, ['walletRequestId', 'referenceNo', 'referenceNumber']),
                    'Date': this.readValue(item, ['dateCreated']),
                    'Partner': this.getFullNameFromObject(item?.partner) || this.readValue(item, ['partnerName']),
                    'Payment Method': this.readNestedValue(item, ['bankAccount.bank', 'paymentMethod', 'method']),
                    'Account Name': this.readNestedValue(item, ['bankAccount.accountName', 'accountName']),
                    'Amount': this.readValue(item, ['amount']),
                    'Status': this.formatPartnerStatus(String(item?.status || '')),
                }));
            case 'coupons':
                return this.downloadGenericReport(moduleKey, args as GenericReportDownloadArgs, 'coupons-report', (item) => ({
                    'Code': this.readValue(item, ['code']),
                    'Title': this.readValue(item, ['title']),
                    'Subtitle': this.readValue(item, ['subTitle', 'subtitle']),
                    'Date': this.readValue(item, ['dateCreated']),
                    'Type': this.readValue(item, ['type']),
                    'Service Type': this.readValue(item, ['service']),
                    'Audience': this.readValue(item, ['audience']),
                    'Qty/Cap': this.readValue(item, ['quantity', 'cap']),
                }));
            case 'rewards':
                return this.downloadGenericReport(moduleKey, args as GenericReportDownloadArgs, 'rewards-report', (item) => ({
                    'ID': this.readValue(item, ['rewardRaffleId', 'id']),
                    'Title': this.readValue(item, ['title']),
                    'Points': this.readValue(item, ['points']),
                    'Start Date': this.readValue(item, ['dateCreated', 'dateStart']),
                    'Audience': this.readValue(item, ['audience']),
                    'Number of Winners': this.readValue(item, ['noWinner']),
                    'Status': this.formatPartnerStatus(String(item?.status || '')),
                }));
            case 'ads':
                return this.downloadGenericReport(moduleKey, args as GenericReportDownloadArgs, 'ads-report', (item) => ({
                    'Ads ID': this.readValue(item, ['adsId', 'id']),
                    'Title': this.readValue(item, ['title']),
                    'Subtitle': this.readValue(item, ['subTitle', 'subtitle']),
                    'Create Date': this.readValue(item, ['dateCreated']),
                    'Published Date': this.readValue(item, ['publishedDate']),
                    'Status': this.formatPartnerStatus(String(item?.status || '')),
                }));
            case 'news_events':
                return this.downloadGenericReport(moduleKey, args as GenericReportDownloadArgs, 'news-events-report', (item) => ({
                    'News ID': this.readValue(item, ['newsEventId', 'newsId', 'id']),
                    'Audience': this.readValue(item, ['audience']),
                    'Type': this.readValue(item, ['type']),
                    'Title': this.readValue(item, ['title']),
                    'Subtitle': this.readValue(item, ['subTitle', 'subtitle']),
                    'Date Created': this.readValue(item, ['dateCreated']),
                    'Status': this.formatPartnerStatus(String(item?.status || '')),
                }));
            default:
                return {
                    message: `Download handler for ${moduleKey} is not available.`,
                };
        }
    }

    private downloadGenericReport(
        moduleKey: DownloadModuleKey,
        args: GenericReportDownloadArgs,
        defaultFileName: string,
        mapper: (item: any) => ExcelRow
    ): DownloadHandlerResult {
        const rows = this.getGenericReportSource(args.data).map(mapper);

        this.excelService.generateExcel(rows, args.fileName || defaultFileName);

        return {
            message: `${this.formatPartnerStatus(String(moduleKey))} report generated.`,
        };
    }

    private getGenericReportSource(data: any[] = []): any[] {
        return data.length ? data : [{}];
    }

    private downloadPartnerReport(args: PartnerReportDownloadArgs): DownloadHandlerResult {
        const rows = args.template === 'template_1'
            ? this.buildPartnerReportTemplateOne(args.data)
            : this.buildPartnerReportTemplateTwo(args.data);
        const fileTemplateName = args.template === 'template_1' ? 'template-1' : 'template-2';
        const reportDate = args.reportDate || this.formatDateForInput(new Date());

        this.excelService.generateExcel(
            rows,
            `partner-report-${fileTemplateName}-${reportDate}`
        );

        return {
            message: `Partner report template ${args.template === 'template_1' ? '1' : '2'} generated.`,
        };
    }

    private buildPartnerReportTemplateOne(data: Rider[]): ExcelRow[] {
        return this.getPartnerReportSource(data).map((rider, index) => ({
            'No.': index + 1,
            'Partner ID': rider.membershipId || rider.partnerId,
            'Partner Name': this.getPartnerFullName(rider),
            'Date Submitted': this.formatReportDate(rider.dateCreated),
            'Time Submitted': this.formatReportTime(rider.dateCreated),
            'Service Type': this.getPartnerServiceTypes(rider),
            'Vehicle type': rider.vehicleType || '-',
            'Status': this.formatPartnerStatus(rider.status),
        }));
    }

    private buildPartnerReportTemplateTwo(data: Rider[]): ExcelRow[] {
        return this.getPartnerReportSource(data).map((rider, index) => ({
            'No.': index + 1,
            'Partner ID': rider.membershipId || rider.partnerId,
            'Partner Name': this.getPartnerFullName(rider),
            'Date Submitted': this.formatReportDate(rider.dateCreated),
            'Time Submitted': this.formatReportTime(rider.dateCreated),
            'Service Type': this.getPartnerServiceTypes(rider),
            'Vehicle type': rider.vehicleType || '-',
            'Docs Completed': this.getCompletedDocumentsLabel(rider),
            'Assigned To': 'Unassigned',
            'Status': this.formatPartnerStatus(rider.status),
            'Last Updated': this.formatReportDate((rider as RiderWithUpdateDate).dateUpdated || rider.dateCreated),
            'Result': this.getPartnerReportResult(rider),
        }));
    }

    private getPartnerReportSource(data: Rider[]): Rider[] {
        if (data.length) {
            return data;
        }

        return [{
            partnerId: '',
            tier: '',
            membershipId: '',
            firstName: '',
            middleName: '',
            lastName: '',
            mobile: '',
            email: '',
            distributionArea: '',
            vehicleType: '',
            licensePlate: '',
            isOtpVerify: false,
            isApprove: false,
            partnerDocuments: [],
            partnerVehicles: [],
            dateCreated: '',
            status: '',
            currentPoints: 0,
            usedPoints: 0,
        }];
    }

    private getPartnerFullName(rider: Rider): string {
        return [rider.firstName, rider.middleName, rider.lastName]
            .filter(Boolean)
            .join(' ') || '-';
    }

    private getPartnerServiceTypes(rider: Rider): string {
        const serviceTypes: string[] = [];
        const vehicle = rider.partnerVehicles?.[0];

        if (vehicle?.isExpress) {
            serviceTypes.push('Express');
        }

        if (vehicle?.isOkRide) {
            serviceTypes.push('OK Ride');
        }

        if (vehicle?.isMototaxi) {
            serviceTypes.push('Mototaxi');
        }

        if (vehicle?.isTaxi) {
            serviceTypes.push('OK! Taxi');
        }

        return serviceTypes.join(', ') || '-';
    }

    private getCompletedDocumentsLabel(rider: Rider): string {
        const totalDocuments = rider.partnerDocuments?.length || 0;
        const completedDocuments = rider.partnerDocuments?.filter((document) => document.isDone).length || 0;

        return `${completedDocuments}/${totalDocuments}`;
    }

    private getPartnerReportResult(rider: Rider): string {
        if (rider.status === 'approved') {
            return 'Approved';
        }

        if (rider.status === 'for_ammendment') {
            return 'For Amendment';
        }

        return '-';
    }

    private readValue(item: any, keys: string[]): string | number {
        for (const key of keys) {
            const value = item?.[key];

            if (value !== undefined && value !== null && value !== '') {
                return value;
            }
        }

        return '-';
    }

    private readNestedValue(item: any, paths: string[]): string | number {
        for (const path of paths) {
            const value = path.split('.').reduce((currentValue, key) => currentValue?.[key], item);

            if (value !== undefined && value !== null && value !== '') {
                return value;
            }
        }

        return '-';
    }

    private getFullNameFromObject(value: any): string {
        if (!value) {
            return '';
        }

        if (value.name) {
            return value.name;
        }

        return [value.firstName, value.middleName, value.lastName]
            .filter(Boolean)
            .join(' ');
    }

    private toNumber(value: any): number {
        const parsedValue = Number(value);

        return Number.isNaN(parsedValue) ? 0 : parsedValue;
    }

    private formatPartnerStatus(status: string): string {
        if (!status) {
            return '-';
        }

        return status
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
    }

    private formatDateForInput(date: Date): string {
        return date.toISOString().slice(0, 10);
    }

    private formatReportDate(dateValue: string): string {
        if (!dateValue) {
            return '-';
        }

        const date = new Date(dateValue);

        if (Number.isNaN(date.getTime())) {
            return dateValue.split(' ')[0] || dateValue;
        }

        return date.toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: '2-digit',
        });
    }

    private formatReportTime(dateValue: string): string {
        if (!dateValue) {
            return '-';
        }

        const date = new Date(dateValue);

        if (Number.isNaN(date.getTime())) {
            const timePart = dateValue.split(' ')[1];
            return timePart || '-';
        }

        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
        });
    }
}
